import React, { useEffect, useRef, useState } from "react";
import { Play, Maximize2, Minimize2, Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";

const URL_RE = /(https?:\/\/[^\s]+)/g;
const isUrl = (s) => /^https?:\/\//i.test(s);
const VIDEO_FILE_RE = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i;
const withAutoplay = (u) => u + (u.includes("?") ? "&" : "?") + "autoplay=1&muted=1&mute=1";

// Any direct video file link autoplays muted inline (looping) when scrolled into
// view — the same "just plays" behaviour as the YouTube embed.
function AutoVideo({ src, poster }) {
  const vidRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [big, setBig] = useState(false);
  useEffect(() => { if (vidRef.current) vidRef.current.muted = muted; }, [muted]);
  useEffect(() => {
    const el = vidRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && e.intersectionRatio >= 0.6) el.play().then(() => setPlaying(true)).catch(() => {});
      else { el.pause(); setPlaying(false); }
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div data-testid="autoplay-video" onClick={(e) => e.stopPropagation()}
      className={`mt-2 relative rounded-xl overflow-hidden border border-[var(--rc-border)] bg-black transition-all ${big ? "w-full" : "max-w-[20rem]"}`}>
      <video ref={vidRef} src={src} poster={poster || undefined} loop playsInline muted preload="metadata"
        onClick={() => { const el = vidRef.current; if (el.paused) { el.play().then(() => setPlaying(true)).catch(() => {}); } else { el.pause(); setPlaying(false); } }}
        className="w-full max-h-[60vh] object-contain bg-black" />
      {!playing && (
        <span className="absolute inset-0 grid place-items-center pointer-events-none">
          <span className="h-12 w-12 grid place-items-center rounded-full bg-black/55"><Play size={22} className="text-white ml-0.5" fill="white" /></span>
        </span>
      )}
      <button onClick={() => setBig((b) => !b)} data-testid="autoplay-video-resize"
        className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
        {big ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
      </button>
      <button onClick={() => setMuted((m) => !m)} data-testid="autoplay-video-mute"
        className="absolute bottom-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
        {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
    </div>
  );
}

// An embeddable player (Vimeo, TikTok, Instagram, Streamable, etc.) autoplays
// muted; the iframe is only mounted while in view (acts as a pause when scrolled away).
function AutoIframe({ src, title, poster, vertical }) {
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [big, setBig] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting && e.intersectionRatio >= 0.5), { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={wrapRef} data-testid="autoplay-embed" onClick={(e) => e.stopPropagation()}
      className={`mt-2 relative rounded-xl overflow-hidden border border-[var(--rc-border)] bg-black transition-all ${big ? "w-full" : (vertical ? "max-w-[15rem]" : "max-w-[20rem]")}`}>
      <div className={`relative ${vertical ? "aspect-[9/16]" : "aspect-video"}`}>
        {inView ? (
          <iframe title={title || "Video"} data-testid="autoplay-embed-iframe" src={withAutoplay(src)}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
        ) : (
          <div className="absolute inset-0">
            {poster ? <img src={poster} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-[var(--rc-surface)]" />}
            <span className="absolute inset-0 grid place-items-center"><span className="h-12 w-12 grid place-items-center rounded-full bg-black/55"><Play size={22} className="text-white ml-0.5" fill="white" /></span></span>
          </div>
        )}
        <button onClick={() => setBig((b) => !b)} data-testid="autoplay-embed-resize"
          className="absolute top-2 right-2 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
          {big ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>
      </div>
    </div>
  );
}

// TikTok / Instagram Reels: build the platform's inline player from the link.
function tiktokId(url) {
  const m = url.match(/tiktok\.com\/(?:@[^/]+\/video|v|embed(?:\/v2)?)\/(\d+)/i) || url.match(/[?&]item_id=(\d+)/i);
  return m ? m[1] : null;
}
function instaEmbed(url) {
  const m = url.match(/instagram\.com\/(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/i);
  if (!m) return null;
  const typ = (m[1] === "reels") ? "reel" : m[1];
  return `https://www.instagram.com/${typ}/${m[2]}/embed/`;
}
// Twitch clips embed inline (16:9). Twitch requires `parent` = the embedding host.
function twitchClipEmbed(url) {
  const m = url.match(/clips\.twitch\.tv\/(?!embed)([A-Za-z0-9_-]+)/i)
    || url.match(/(?:www\.|m\.)?twitch\.tv\/[^/]+\/clip\/([A-Za-z0-9_-]+)/i);
  if (!m) return null;
  const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "localhost";
  return `https://clips.twitch.tv/embed?clip=${m[1]}&parent=${host}&autoplay=true&muted=true`;
}
// Facebook videos/reels embed inline via the official video plugin (fb.watch short
// links are resolved server-side instead).
function facebookEmbed(url) {
  if (!/facebook\.com\/(watch\/?\?v=|reel\/|[^/]+\/videos\/|video\.php)/i.test(url)) return null;
  return { src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=true&mute=1`, vertical: /facebook\.com\/reel\//i.test(url) };
}

function ytId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split(/[/?]/)[0] || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const m = u.pathname.match(/\/(shorts|embed)\/([^/?]+)/);
      if (m) return m[2];
    }
  } catch (e) {}
  return null;
}

function YouTubeCard({ url, id }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let active = true;
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setInfo(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [url]);
  const thumb = info?.thumbnail_url || `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="youtube-preview" onClick={(e) => e.stopPropagation()}
      className="mt-2 block max-w-[16rem] rounded-xl overflow-hidden border border-[var(--rc-border)] bg-black/25 hover:border-[var(--rc-gold)]/50 transition-colors">
      <div className="relative">
        <img src={thumb} alt="" className="w-full aspect-video object-cover" />
        <span className="absolute inset-0 grid place-items-center">
          <span className="h-11 w-11 grid place-items-center rounded-full bg-black/60">
            <Play size={20} className="text-white ml-0.5" fill="white" />
          </span>
        </span>
      </div>
      <div className="p-2">
        <div className="text-xs font-medium text-[var(--rc-text)] line-clamp-2">{info?.title || "YouTube video"}</div>
        <div className="text-[10px] text-[var(--rc-muted)] mt-0.5">▶ YouTube{info?.author_name ? ` · ${info.author_name}` : ""}</div>
      </div>
    </a>
  );
}

// In-app YouTube player: autoplays muted (inline) when scrolled into view, like a
// regular feed video. Includes a resize toggle (compact ↔ theater) plus YouTube's
// own controls/fullscreen. Pauses automatically when scrolled out of view.
function YouTubeEmbed({ url, id }) {
  const [info, setInfo] = useState(null);
  const [big, setBig] = useState(false);
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);
  useEffect(() => {
    let active = true;
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setInfo(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [url]);

  const command = (func) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args: [] }), "*");
    } catch (e) {}
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e.isIntersecting && e.intersectionRatio >= 0.6) command("playVideo");
      else command("pauseVideo");
    }, { threshold: [0, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const src = `https://www.youtube.com/embed/${id}?enablejsapi=1&autoplay=1&mute=1&controls=1&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${id}`;
  return (
    <div ref={wrapRef} data-testid="youtube-embed" onClick={(e) => e.stopPropagation()}
      className={`mt-2 rounded-xl overflow-hidden border border-[var(--rc-border)] bg-black transition-all ${big ? "w-full" : "max-w-[20rem]"}`}>
      <div className="relative aspect-video">
        <iframe ref={iframeRef} title="YouTube" data-testid="youtube-iframe" src={src}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen />
        <button onClick={() => setBig((b) => !b)} data-testid="youtube-resize"
          className="absolute top-2 right-2 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80">
          {big ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
        </button>
      </div>
      <div className="p-2">
        <div className="text-xs font-medium text-white line-clamp-2">{info?.title || "YouTube video"}</div>
        <div className="text-[10px] text-white/60 mt-0.5">▶ YouTube{info?.author_name ? ` · ${info.author_name}` : ""}</div>
      </div>
    </div>
  );
}

function PreviewCard({ url, info }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="link-preview" onClick={(e) => e.stopPropagation()}
      className="mt-2 flex max-w-[17rem] rounded-xl overflow-hidden border border-[var(--rc-border)] bg-black/25 hover:border-[var(--rc-gold)]/50 transition-colors">
      {info.image && (
        <img src={info.image} alt="" className="w-20 h-auto self-stretch object-cover shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}
      <div className="p-2 min-w-0 flex-1">
        {info.title && <div className="text-xs font-medium text-[var(--rc-text)] line-clamp-2">{info.title}</div>}
        {info.description && <div className="text-[11px] text-[var(--rc-muted)] line-clamp-2 mt-0.5">{info.description}</div>}
        {info.site && <div className="text-[10px] text-[var(--rc-gold)]/70 mt-1 truncate">{info.site}</div>}
      </div>
    </a>
  );
}

function LinkCard({ url }) {
  const [info, setInfo] = useState(undefined); // undefined=loading, null=nothing to show
  useEffect(() => {
    let active = true;
    api.get("/link-preview", { params: { url } })
      .then(({ data }) => { if (active) setInfo(data && (data.title || data.image || data.video_src || data.video_embed) ? data : null); })
      .catch(() => { if (active) setInfo(null); });
    return () => { active = false; };
  }, [url]);
  if (info === undefined) return <div className="mt-2 h-16 max-w-[17rem] rounded-xl bg-black/20 animate-pulse" data-testid="link-preview-loading" />;
  if (!info) return null;
  if (info.video_src) return <AutoVideo src={info.video_src} poster={info.image} />;
  if (info.video_embed) return <AutoIframe src={info.video_embed} title={info.title} poster={info.image} vertical={info.video_vertical} />;
  return <PreviewCard url={url} info={info} />;
}

export default function LinkifiedText({ text, className, preview, embedYouTube }) {
  if (!text) return null;
  const parts = text.split(URL_RE);
  const urls = text.match(URL_RE) || [];
  const yt = urls.map((u) => ({ u, id: ytId(u) })).find((x) => x.id);
  const firstLink = urls.find((u) => !ytId(u));
  const directVid = firstLink && VIDEO_FILE_RE.test(firstLink) ? firstLink : null;
  const tkId = firstLink ? tiktokId(firstLink) : null;
  const igEmbed = firstLink ? instaEmbed(firstLink) : null;
  const twEmbed = firstLink ? twitchClipEmbed(firstLink) : null;
  const fbEmbed = firstLink ? facebookEmbed(firstLink) : null;
  const hasEmbedded = preview && (preview.title || preview.image);
  return (
    <>
      <p className={className}>
        {parts.map((part, i) =>
          isUrl(part) ? (
            <a key={i} href={part} target="_blank" rel="noreferrer" data-testid="msg-link" onClick={(e) => e.stopPropagation()}
              className="text-[var(--rc-gold)] underline break-all hover:opacity-80">{part}</a>
          ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
          )
        )}
      </p>
      {yt ? (
        embedYouTube ? <YouTubeEmbed url={yt.u} id={yt.id} /> : <YouTubeCard url={yt.u} id={yt.id} />
      ) : directVid ? (
        <AutoVideo src={directVid} />
      ) : (preview && preview.video_src) ? (
        <AutoVideo src={preview.video_src} poster={preview.image} />
      ) : (preview && preview.video_embed) ? (
        <AutoIframe src={preview.video_embed} title={preview.title} poster={preview.image} vertical={preview.video_vertical} />
      ) : tkId ? (
        <AutoIframe src={`https://www.tiktok.com/player/v1/${tkId}?autoplay=1&muted=1&controls=1&loop=1`} vertical />
      ) : igEmbed ? (
        <AutoIframe src={igEmbed} vertical />
      ) : twEmbed ? (
        <AutoIframe src={twEmbed} title="Twitch clip" />
      ) : fbEmbed ? (
        <AutoIframe src={fbEmbed.src} title="Facebook video" vertical={fbEmbed.vertical} />
      ) : hasEmbedded ? (
        <PreviewCard url={preview.url || firstLink} info={preview} />
      ) : firstLink ? (
        <LinkCard url={firstLink} />
      ) : null}
    </>
  );
}
